import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../../../utils/styledComponent';
import searchMatch from '../../../utils/searchMatch';
import theme from '../../../theme';
import DropdownTrigger from '../../../components/atoms/forms/DropdownTrigger';
import ListOption from '../../../components/atoms/ListOption';
import { accentClassVariants } from './statusStyles';

// Generic searchable dropdown for the Monitoring tab's driver/route pickers.
// One trigger button opens a menu with a search input, a pinned "all" row,
// and status-dotted item rows. DriverPicker/RoutePicker are thin wrappers
// that supply the item accessors and copy.
const StyledDiv = styledComponent('div', {
  position: 'relative',
  display: 'inline-block',

  '& .ep-menu': {
    position: 'absolute',
    top: 'calc(100% + 6px)',
    right: 0,
    minWidth: '260px',
    background: theme.colors.bgCard,
    border: `1px solid ${theme.colors.borderLight}`,
    borderRadius: theme.radii.inner,
    boxShadow: theme.shadows.dropdown,
    zIndex: 1000,
    overflow: 'hidden',
  },
  '& .ep-menu.ep-menu-left': {
    right: 'auto',
    left: 0,
  },
  '& .ep-search': {
    position: 'relative',
    padding: '8px',
    borderBottom: `1px solid ${theme.colors.borderLight}`,
  },
  '& .ep-search-icon': {
    position: 'absolute',
    left: '18px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: theme.colors.textHint,
    pointerEvents: 'none',
  },
  '& .ep-search-input': {
    width: '100%',
    padding: '6px 8px 6px 28px',
    fontSize: theme.sizes.smallFontSize,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.radii.input,
    color: theme.colors.textBody,
    background: theme.colors.bgCard,
  },
  '& .ep-search-input:focus': {
    outline: 'none',
    borderColor: theme.colors.infoBlue,
    boxShadow: `0 0 0 2px ${theme.colors.infoBlue}33`,
  },

  '& .ep-list': {
    maxHeight: '300px',
    overflowY: 'auto',
    padding: '4px',
  },
  '& .ep-dot': {
    width: '8px',
    height: '8px',
    borderRadius: theme.radii.circle,
    flex: '0 0 auto',
  },
  ...accentClassVariants('ep-dot', 'background'),
  '& .ep-item-name': {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& .ep-item-mini': {
    fontSize: theme.sizes.captionFontSize,
    fontWeight: theme.fontWeights.medium,
    color: theme.colors.textMuted,
    padding: '1px 6px',
    borderRadius: theme.radii.pill,
    background: theme.colors.bgMuted,
    fontVariantNumeric: 'tabular-nums',
  },
  '& .ep-empty': {
    padding: '16px',
    fontSize: theme.sizes.smallFontSize,
    color: theme.colors.textMuted,
    fontStyle: 'italic',
    textAlign: 'center',
  },
});

const SearchGlyph = ({ className }) => (
  <svg
    className={className}
    width="12"
    height="12"
    viewBox="0 0 14 14"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    aria-hidden="true"
  >
    <circle cx="6" cy="6" r="4.5" />
    <path d="M10 10l3 3" strokeLinecap="round" />
  </svg>
);

SearchGlyph.propTypes = {
  className: PropTypes.string,
};

SearchGlyph.defaultProps = {
  className: undefined,
};

function EntityPicker({
  items,
  selectedId,
  onChange,
  getItemId,
  getItemKey,
  getItemStatus,
  getItemName,
  getItemMini,
  getSearchText,
  triggerLabel,
  triggerSearchIcon,
  allLabel,
  allMini,
  emptyText,
  searchPlaceholder,
  searchAriaLabel,
  align,
}) {
  // Row identity defaults to the selection id; pass getItemKey only when the
  // two differ (DriverPicker keys rows by routeId but selects by driverId).
  const keyOf = getItemKey || getItemId;
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      return undefined;
    }
    if (inputRef.current) inputRef.current.focus();
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen]);

  const filtered = useMemo(() => {
    const needle = searchTerm.trim();
    if (!needle) return items;
    return items.filter((item) => searchMatch(getSearchText(item), needle));
  }, [items, searchTerm, getSearchText]);

  const handleSelect = (id) => {
    onChange(id);
    setIsOpen(false);
  };

  return (
    <StyledDiv ref={wrapperRef}>
      <DropdownTrigger
        label={triggerLabel}
        onClick={() => setIsOpen((v) => !v)}
        isOpen={isOpen}
        icon={triggerSearchIcon ? <SearchGlyph /> : null}
      />

      {isOpen && (
        <div
          className={`ep-menu${align === 'left' ? ' ep-menu-left' : ''}`}
          role="listbox"
        >
          <div className="ep-search">
            <SearchGlyph className="ep-search-icon" />
            <input
              ref={inputRef}
              type="text"
              className="ep-search-input"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label={searchAriaLabel}
            />
          </div>
          <div className="ep-list">
            <ListOption
              onClick={() => handleSelect(null)}
              selected={selectedId === null}
            >
              <span className="ep-item-name">{allLabel}</span>
              <span className="ep-item-mini">{allMini}</span>
            </ListOption>
            {filtered.length === 0 && (
              <div className="ep-empty">{emptyText}</div>
            )}
            {filtered.map((item) => {
              const id = getItemId(item);
              return (
                <ListOption
                  key={keyOf(item)}
                  onClick={() => handleSelect(id)}
                  selected={selectedId === id}
                >
                  <span
                    className={`ep-dot ${getItemStatus(item) || ''}`.trim()}
                  />
                  <span className="ep-item-name">{getItemName(item)}</span>
                  <span className="ep-item-mini">{getItemMini(item)}</span>
                </ListOption>
              );
            })}
          </div>
        </div>
      )}
    </StyledDiv>
  );
}

EntityPicker.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedId: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  getItemId: PropTypes.func.isRequired,
  getItemKey: PropTypes.func,
  getItemStatus: PropTypes.func.isRequired,
  getItemName: PropTypes.func.isRequired,
  getItemMini: PropTypes.func.isRequired,
  getSearchText: PropTypes.func.isRequired,
  triggerLabel: PropTypes.node.isRequired,
  triggerSearchIcon: PropTypes.bool,
  allLabel: PropTypes.string.isRequired,
  allMini: PropTypes.node.isRequired,
  emptyText: PropTypes.string.isRequired,
  searchPlaceholder: PropTypes.string.isRequired,
  searchAriaLabel: PropTypes.string.isRequired,
  align: PropTypes.oneOf(['left', 'right']),
};

EntityPicker.defaultProps = {
  selectedId: null,
  getItemKey: null,
  triggerSearchIcon: false,
  align: 'right',
};

export default EntityPicker;
